import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface SellerPresence {
  isOnline: boolean;
  lastSeen: Date | null;
}

export const useSellerPresence = (userId: string | undefined) => {
  const { currentUser } = useAuth();
  const [sellerPresence, setSellerPresence] = useState<SellerPresence>({ isOnline: false, lastSeen: null });

  useEffect(() => {
    if (!userId) return;

    let unsubscribe: (() => void) | null = null;

    const subscribeToPresence = async () => {
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        unsubscribe = await messagingService.subscribeToUserPresence(
          userId,
          (presence) => {
            setSellerPresence({
              isOnline: presence.isOnline,
              lastSeen: presence.lastSeen ? presence.lastSeen.toDate() : null
            });
          }
        );
      } catch (error) {
        // Removed debug log to prevent sensitive data exposure
      }
    };

    subscribeToPresence();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userId]);

  // Set presence on mount and handle visibility changes
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) return; // Skip if user not authenticated

    const setPresence = async () => {
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        await messagingService.updateUserPresence(uid, true);
      } catch (error) {
        // Removed debug log to prevent sensitive data exposure
      }
    };

    setPresence();

    const handleVisibilityChange = async () => {
      const currentUid = currentUser?.uid;
      if (!currentUid) return;
      
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        if (document.hidden) {
          await messagingService.updateUserPresence(currentUid, false);
        } else {
          await messagingService.updateUserPresence(currentUid, true);
        }
      } catch (error) {
        // Removed debug log to prevent sensitive data exposure
      }
    };

    const handleBeforeUnload = async () => {
      const currentUid = currentUser?.uid;
      if (!currentUid) return;
      
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        await messagingService.updateUserPresence(currentUid, false);
      } catch (error) {
        // Removed debug log to prevent sensitive data exposure
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Set offline on cleanup
      const cleanup = async () => {
        const currentUid = currentUser?.uid;
        if (!currentUid) return;
        
        try {
          const { messagingService } = await import('../services/firebaseConfig');
          await messagingService.updateUserPresence(currentUid, false);
        } catch (error) {
          // Removed debug log to prevent sensitive data exposure
        }
      };
      cleanup();
    };
  }, [currentUser?.uid]);

  const formatLastSeen = (lastSeen: Date): string => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return {
    sellerPresence,
    formatLastSeen
  };
};


